'use client'

/**
 * Landing page — Profileia public home.
 *
 * SPEC-035 §2.3.1 PO-07 — ISR (revalidate 60s) NOT APPLICABLE HERE.
 *
 * Reason: This page is a Client Component (`'use client'`) and renders all
 * content (plans, features, mockups) from hardcoded constants — there is NO
 * `fetch()` to any backend API or DB. Next.js ISR requires a Server Component
 * that fetches data at request/render time (so Next.js can cache + revalidate
 * the rendered HTML); without a data fetch, the page is already 100% static
 * and is pre-rendered at build time, which gives the same TTFB benefit that
 * ISR would provide for a dynamic page.
 *
 * To verify the static-render behaviour, the page already uses Next.js
 * `<Image>` for the local logo assets (PO-08) and ships no server data fetch.
 * If this page ever starts fetching dynamic content (e.g. testimonials from
 * the backend), convert it to a Server Component and add:
 *
 *   export const revalidate = 60; // SPEC-035 §2.3.1 PO-07 — ISR 60s
 *
 * For now, the page is build-time static — equivalent or better than ISR.
 */

import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { AIChatWidget } from '@/components/ai/ai-chat-widget'
import Image from 'next/image'
import {
  CreditCard, Video, FileText, DollarSign,
  Users, Zap, Shield, ArrowRight, Check, Star,
  Monitor, Sun, Moon,
  Eye, TrendingUp,
  GraduationCap, ShoppingBag, Brain, Sparkles,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { InstallAppButton } from '@/components/pwa/InstallAppButton'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
// SPEC-205 (Sprint 101 — Casa Unificada): Navbar unificado reemplaza el
// navbar inline. 6 ítems canónicos: Inicio · Ecosistema · AI Pro Army ·
// Compensación · Planes · Acceder (decisión #5 del Founder).
import { Navbar } from '@/components/layout/Navbar'
// SPEC-136 (Sprint 62 — P-219): TestimonialsSection — landing zona
// testimonios (between pricing and CTA final). Renders top 6 featured
// testimonials with Schema.org Review markup. Anti-§6: section copy uses
// 'historias reales' NOT 'ganancias garantizadas' (per §13).
import { TestimonialsSection } from '@/components/landing/TestimonialsSection'
// V126-G2 (Founder directive 2026-10-22): FOMO stats bar with animated counters.
import { StatsBar } from '@/components/landing/stats-bar'
import { LandingFeaturedMarketplace } from '@/components/landing/landing-featured-marketplace'
import { LandingFeaturedCoupons } from '@/components/landing/landing-featured-coupons'
import { SectionErrorBoundary } from '@/components/landing/SectionErrorBoundary'
import { AdBannerHorizontal } from '@/components/ads/AdSlot'

const plans = [
  // M-14 fix: Add Gratuito (Free) tier at the beginning
  // Sprint 64 (SPEC-141 — Founder directive 2026-09-07): updated values
  // verbatim from Founder chat. Créditos PM = 2x precio USDT excepto
  // Diamante 1.5x (mayor ahorro). Badges: Oro "Más popular", Diamante "MAYOR AHORRO".
  {
    name: 'Gratuito',
    price: 0,
    duration: '',
    spectators: 10,
    monthly: 'Gratis',
    priceCreditsPM: null,
    features: ['Conference (limitada)', '10 espectadores', '30 min/sala', 'Perfil público', 'Share Screen', 'Capacitaciones Profileia'],
    popular: false,
    bestValue: false,
  },
  {
    name: 'Basic',
    price: 20,
    duration: '30 días',
    spectators: 100,
    monthly: null,
    priceCreditsPM: 40,
    features: ['Conference Room', '100 espectadores', 'Chat en vivo', 'Grabación local', '20 cursos de Emprendimiento'],
    popular: false,
    bestValue: false,
  },
  {
    name: 'Bronce',
    price: 50,
    duration: '60 días',
    spectators: 200,
    monthly: '$25.00/mes equivalente',
    priceCreditsPM: 100,
    features: ['Todo en Basic', '+ Tarjeta Digital', '+ 200 espectadores', '+ Marca personal', '+ 50 cursos de Emprendimiento'],
    popular: false,
    bestValue: false,
  },
  {
    name: 'Plata',
    price: 100,
    duration: '90 días',
    spectators: 200,
    monthly: '$33.33/mes equivalente',
    priceCreditsPM: 200,
    features: ['Todo en Bronce', '+ Landing de Captura', '+ Captura de prospectos', '+ Código QR', '+ 100 cursos de Emprendimiento'],
    popular: false,
    bestValue: false,
  },
  {
    name: 'Oro',
    price: 250,
    duration: '120 días',
    spectators: 500,
    monthly: '$62.50/mes equivalente',
    priceCreditsPM: 500,
    features: ['Todo en Plata', '+ 500 espectadores', '+ Marca personalizada en sala', '+ 250 cursos de Emprendimiento'],
    popular: true,
    bestValue: false,
  },
  {
    name: 'Platino',
    price: 500,
    duration: '180 días',
    spectators: 1000,
    monthly: '$83.33/mes equivalente',
    priceCreditsPM: 1000,
    features: ['Todo en Oro', '+ 1,000 espectadores', '+ Herramientas pro', '+ Estadísticas avanzadas', '+ 500 cursos de Emprendimiento'],
    popular: false,
    bestValue: false,
  },
  {
    name: 'Diamante',
    price: 1000,
    duration: '365 días',
    spectators: 1000,
    monthly: '$82.19/mes equivalente',
    priceCreditsPM: 1500,
    features: ['Suite completa', '+ Compensación total', '+ Menor tarifa diaria', '+ 1000+ cursos de Emprendimiento', '+ Acceso anticipado a futuros cursos y mejoras'],
    popular: false,
    bestValue: true,
  },
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
}

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
}

export default function LandingPage() {
  const router = useRouter()
  // SPEC-205 (Sprint 101): navbar inline eliminado — ahora vive en <Navbar />.
  // mounted + mobileMenuOpen + dark mode toggle se mantienen en Navbar.tsx.

  // Auth navigation — redirect to real /login and /register pages.
  // Spec ref: SPEC-003-FRONTEND-BACKEND-INTEGRATION §3.2
  // (antes: dialog modal con formularios mock)
  // SPEC-050 Bug 5: pricing-section CTAs now link to `/paquetes` (the
  // public pricing page) instead of `/register` so the user can see
  // details + comparison + compensation matrix before committing to
  // register. The hero "Comenzar Gratis" CTA still points to `/register`
  // (that's for the free tier quick-register).
  // Note: `/paquetes` is a brand new route — with `typedRoutes: true`
  // (next.config.ts:113), TypeScript needs a cast until Next.js
  // regenerates the typed-routes manifest at the next `next dev` /
  // `next build`. Same pattern as `login/page.tsx:92` for the
  // `?next=` redirect URL.
  // SPEC-177 Task C: goLogin removed — navbar CTAs now use <Link href="/login">
  // directly (Button asChild wraps the Link). goRegister + goPaquetes kept for
  // other call-to-actions that still use router.push (hero buttons, etc.).
  const goRegister = () => router.push('/register')
  const goPaquetes = () => router.push('/paquetes' as Parameters<typeof router.push>[0])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  // SPEC-144 (Sprint 67, 2026-09-07) — P-253: expanded Organization JSON-LD
  // + WebSite JSON-LD for the home page. The TestimonialsSection (rendered
  // below) emits a minimal `Organization` JSON-LD that was kept for backwards
  // compatibility (SPEC-139 P-227 fix), but this expanded version on the home
  // page provides the full Schema.org Organization properties (url, logo,
  // sameAs social links, founder, description, contactPoint) plus the
  // WebSite type with publisher. Search engines can deduplicate the two
  // Organization entries by `@id`/url.
  //
  // §6 Anti-Invención: `sameAs` URLs use the SAME real Founder-provided
  // social URLs as the Footer component (instagram.com/profileiaoficial,
  // etc.) — Founder-provided real URLs verified 2026-09-17. These are
  // the canonical Profileia social URLs. Founder name (John Martínez) is
  // the actual project operator. Email soporte@profileia.com is the
  // intended support address (PENDIENTE-FOUNDER #26 until SMTP is
  // configured).
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Profileia',
    url: 'https://profileia.com',
    description: 'Tu perfil inteligente. La casa del emprendedor. Profileia une academia, herramientas, marketplace y compensación en un solo perfil inteligente.',
    founder: {
      '@type': 'Person',
      name: 'John Martínez',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'soporte',
      email: 'soporte@profileia.com',
      availableLanguage: ['es'],
    },
    logo: {
      '@type': 'ImageObject',
      url: 'https://profileia.com/logo/isotipo-fullcolor-512x512.png',
      width: 512,
      height: 512,
    },
    sameAs: [
      'https://www.instagram.com/profileiaoficial?stkn=cmV3dHUzajVzd2x2',
      'https://youtube.com/@profileiaoficial?si=S901cdkSwEd6WdPB',
      'https://www.tiktok.com/@profileiaoficial?_r=1&_t=ZS-99jbjF5g98W',
      'https://x.com/Profileia',
      'https://www.linkedin.com/in/profileia-oficial-87b2ab437',
      'https://www.facebook.com/share/1LVXaGQXBg/',
      'https://t.me/profileiaoficial',
    ],
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Profileia',
    url: 'https://profileia.com',
    inLanguage: 'es',
    publisher: {
      '@type': 'Organization',
      name: 'Profileia',
      url: 'https://profileia.com',
    },
  };

  return (
    <div className="min-h-screen bg-background">
 {/* P-253: Organization + WebSite JSON-LD for the home page.
          Rendered server-side via Next.js SSR for Client Components so
          crawlers see it in the initial HTML (no hydration dependency). */}
      <script
        type="application/ld+json"
        // JSON-LD is a static serialized object (no user input — safe).
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        // JSON-LD is a static serialized object (no user input — safe).
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }}
      />
 {/* ===== NAVBAR ( — Navbar unificado) ===== */}
      <Navbar />

      <main id="main-content">
        {/* ===== HERO ===== */}
 {/* Task G (Sprint 91): id="hero" added so the section is
            anchorable (#hero) — previously the hero had no id, breaking
            deep-links from email campaigns / external sites. */}
        <section id="hero" className="relative overflow-hidden">
          {/* Background orbs */}
          <div className="absolute top-20 -left-32 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 -right-32 w-96 h-96 bg-[#8B5CF6]/10 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div initial="hidden" animate="visible" variants={stagger}>
                <motion.div variants={fadeUp}>
                  <Badge variant="secondary" className="mb-4 bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20">
                    <Zap className="size-3 mr-1" /> La casa del emprendedor
                  </Badge>
                </motion.div>
                <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                  <span className="bg-gradient-to-r from-[#2563EB] via-[#06B6D4] to-[#8B5CF6] bg-clip-text text-transparent">Tu perfil inteligente.</span>
                </motion.h1>
                <motion.p variants={fadeUp} className="mt-6 text-lg text-muted-foreground max-w-lg">
                  Todo lo que necesitas para emprender en un solo lugar. Academia, marketplace, compensación y un ejército de Agentes IA que trabajan para ti — todo desde tu perfil inteligente.
                </motion.p>
                <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-4">
                  <Button size="lg" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" onClick={goRegister}>
                    Comenzar Gratis <ArrowRight className="ml-2 size-4" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => scrollTo('funciones')}>
                    Ver Funciones
                  </Button>
 {/* (P-173) — botón instalar app persistente en landing */}
                  <InstallAppButton variant="outline" size="lg" />
                </motion.div>
                <motion.div variants={fadeUp} className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Check className="size-4 text-green-500" /> Sin tarjeta de crédito</span>
                  <span className="flex items-center gap-1.5"><Check className="size-4 text-green-500" /> 0% comisión en depósitos</span>
                </motion.div>
                {/* V126-G2: FOMO stats bar — animated counters with real platform data */}
                <motion.div variants={fadeUp} className="mt-10">
                  <StatsBar />
                </motion.div>
              </motion.div>

              {/* Hero Mockup */}
 {/* Task I (Sprint 91): add a "Demo" badge above the
                  mockup to clarify that "John Martínez / 847 espectadores /
                  En vivo ahora" is EXAMPLE data (Decision Co-Founder QA #5).
                  Without the badge, visitors could think Profileia is
                  showing a real live conference + a real user's profile —
 misleading advertising per §6 Anti-Invención. */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative hidden lg:block"
              >
                <div className="absolute -top-3 left-4 z-10 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg" aria-label="Ejemplo de Profileia Hub">
                  Demo
                </div>
                <div className="relative rounded-2xl border border-border/50 bg-gradient-to-br from-[#2563EB]/5 via-background to-[#8B5CF6]/5 p-6 shadow-2xl">
 {/* P2-3 (Sprint 91 QA): hidden <Image> for SEO. The hero
                      mockup uses divs + SVG icons (no real <img>), so Google Image
                      Search can't index any visual. This hidden <Image> uses the logo
                      with a descriptive alt — invisible to users (sr-only + 1x1px)
                      but crawlable. Helps with brand image indexing + structured
                      data association. Uses next/image for optimization. */}
                  <Image
                    src="/logo/isotipo-fullcolor-512x512.png"
                    alt="Profileia — Tu perfil inteligente. La casa del emprendedor. Academia, marketplace, salas de conferencia, tarjeta digital y plan de compensación."
                    width={1}
                    height={1}
                    className="sr-only"
                    aria-hidden="false"
                    loading="eager"
                  />
                  {/* Mock Card */}
                  <div className="rounded-xl bg-white dark:bg-[#0B1437] border border-border/50 p-4 shadow-lg mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#8B5CF6]" />
                      <div>
                        <div className="font-semibold text-sm">John Martínez</div>
                        <div className="text-xs text-muted-foreground">profileia.com/john</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {['LinkedIn', 'WhatsApp', 'YouTube'].map(s => (
                        <div key={s} className="px-2 py-1 rounded-md bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium">{s}</div>
                      ))}
                    </div>
                  </div>
                  {/* Mock Conference */}
                  <div className="rounded-xl bg-white dark:bg-[#0B1437] border border-border/50 p-4 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-xs font-medium">En vivo ahora</span>
                      </div>
                      <span className="text-xs text-muted-foreground">847 espectadores</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="aspect-video rounded-lg bg-gradient-to-br from-[#2563EB]/20 to-[#06B6D4]/20 flex items-center justify-center">
                        <Video className="size-6 text-[#2563EB]" />
                      </div>
                      <div className="aspect-video rounded-lg bg-gradient-to-br from-[#8B5CF6]/20 to-[#2563EB]/20 flex items-center justify-center">
                        <Video className="size-6 text-[#8B5CF6]" />
                      </div>
                    </div>
                  </div>
                  {/* Floating badge */}
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' as const }}
                    className="absolute -top-3 -right-3 bg-[#06B6D4] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg"
                  >
                    $0 grabación
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== PROBLEM ===== */}
 {/* Task G (Sprint 91): id="problema" added so the section
            is anchorable (#problema) — used by deep-links + future analytics
            scroll-tracking. Previously the section had no id. */}
        <section id="problema" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <Badge variant="secondary" className="mb-3 bg-destructive/10 text-destructive border-destructive/20">El problema</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold">El emprendedor paga demasiado por herramientas separadas</h2>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: <CreditCard className="size-5" />,
                    title: '$184/mes en herramientas separadas',
                    desc: 'Zoom Pro + Large Meeting + Linktree + Calendly. Y después de pagar, no hay integración entre ninguna.',
                    color: '#2563EB',
                  },
                  {
                    icon: <Eye className="size-5" />,
                    title: 'Sin integración entre las herramientas',
                    desc: 'El prospecto entra a tu Zoom pero no ve tu tarjeta. Hace click en tu Linktree pero no puede agendar. Cada punto de contacto está aislado.',
                    color: '#8B5CF6',
                  },
                  {
                    icon: <TrendingUp className="size-5" />,
                    title: 'Solo gastas, nunca ganas',
                    desc: 'Zoom, Linktree, Calendly, cursos — todos cobran. Compras productos y ninguno te paga. Con Profileia, compras productos en el Market y activas tu negocio MLM.',
                    color: '#06B6D4',
                  },
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeUp}>
                    <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-border/50">
                      <CardContent className="p-6">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
                          {item.icon}
                        </div>
                        <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

 {/* ===== VISIÓN ( — flujo canónico) ===== */}
 {/* Doc 02 §2 manda: problema → visión → ecosistema → prueba → economía → oportunidad.
            Esta sección nueva cumple el paso "visión" del flujo. */}
        <section id="vision" className="py-20 bg-[#0B1437] text-white relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#2563EB]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#8B5CF6]/20 rounded-full blur-3xl" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
              <Sparkles className="size-3 mr-1" /> Nuestra visión
            </Badge>
            <h2 className="text-3xl sm:text-5xl font-bold mb-6 leading-tight">
              Ser la casa del emprendedor:
              <br />
              <span className="bg-gradient-to-r from-[#2563EB] via-[#06B6D4] to-[#8B5CF6] bg-clip-text text-transparent">
                el perfil inteligente que todo emprendedor abra cada mañana
              </span>
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto text-lg">
              Para construir su libertad, sin barreras de costo, idioma ni opacidad.
              Tu identidad, tus herramientas, tus clientes, tus pagos, tu comunidad,
              tu aprendizaje, tus asistentes de IA y tus oportunidades de crecimiento
              — todos en un solo enlace.
            </p>
            <p className="text-white/50 text-sm italic max-w-xl mx-auto">
              "No te prometemos hacerte Multimillonario. Te prometemos no hacerte perder el tiempo."
            </p>
          </div>
        </section>

        {/* ===== FUNCIONES ===== */}
        <section id="funciones" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <Badge variant="secondary" className="mb-3 bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20">
                  <Zap className="size-3 mr-1" /> Profileia lo resuelve
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold">7 módulos, 1 perfil inteligente</h2>
                <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">La integración es la innovación. Cada módulo por separado ya existe — lo que no existe es la integración nativa diseñada para el emprendedor que quiere crecer. AI Pro Army es la capa transversal de IA que potencia todos los módulos.</p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: <Brain className="size-6" />,
                    title: 'Profileia Hub',
                    desc: 'Módulo #1 — tu centro de operaciones. INCLUYE tu Tarjeta Digital (vista pública con QR, vCard, redes sociales y marca) + Conecta (directorio público de profesionales verificados) + accesos directos a todas las herramientas. AI Pro Army (26 Agentes IA) es la capa transversal que potencia tu Hub.',
                    color: '#3B82F6',
                  },
                  {
                    icon: <Sparkles className="size-6" />,
                    title: 'AI Pro Army',
                    desc: 'Capa transversal de IA — 26 agentes IA especializados que trabajan 24/7 para ti: CEO (Valeria), COO (Marcos), Contadora, Abogada, CM, Ventas, Marketing, RRHH, Copywriter, Creativos PRO y más. Recuerdan tu negocio, leen tus documentos y se coordinan entre sí.',
                    color: '#8B5CF6',
                  },
                  {
                    icon: <Video className="size-6" />,
                    title: 'Conference',
                    desc: 'Presenta a 1,000 personas con marca personalizada. 4 presentadores en todos los paquetes, chat, compartir pantalla, grabación local $0. Salas automatizadas reproducen tus presentaciones grabadas 24/7.',
                    color: '#06B6D4',
                  },
                  {
                    icon: <FileText className="size-6" />,
                    title: 'Landing de Captura',
                    desc: 'Captura prospectos sin salir de tu ecosistema. Formularios, WhatsApp, testimonios y botón "Entrar a conferencia". Dominio personalizado opcional.',
                    color: '#8B5CF6',
                  },
                  {
                    icon: <GraduationCap className="size-6" />,
                    title: 'Academia',
                    desc: 'Capacitaciones del sistema gratis para todos + 1.000 modelos de negocio organizados en rutas de 20 módulos según tu paquete + marketplace de cursos tipo Hotmart donde puedes vender tus propios cursos.',
                    color: '#F59E0B',
                  },
                  {
                    icon: <ShoppingBag className="size-6" />,
                    title: 'Profileia Market',
                    desc: 'Compra con créditos PM (1 crédito = 1 USDT) y vende productos físicos, digitales y servicios. Modelo estándar 30/2/68 o cupones 50-50 (vendedor recibe 65% total: 15% créditos + 50% directo).',
                    color: '#10B981',
                  },
                  {
                    icon: <DollarSign className="size-6" />,
                    title: 'Matriz Viva',
                    desc: 'Módulo #7 — 2×6 con bono directo $10, compresión dinámica, retiros cripto y escalación Diamante ilimitada. Solo Networkers participan; Cliente y Gratuito NO ocupan posición en matriz.',
                    color: '#0EA5E9',
                  },
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeUp}>
                    <Card className="h-full group hover:shadow-lg transition-all duration-300 border-border/50 overflow-hidden">
                      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${item.color}, ${item.color}80)` }} />
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
                          {item.icon}
                        </div>
                        <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ===== PRECIOS ===== */}
        <section id="precios" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <Badge variant="secondary" className="mb-3 bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20">
                  <Star className="size-3 mr-1" /> Precios
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold">Elige tu plan</h2>
                <p className="mt-4 text-muted-foreground">Desde $20 hasta $1,000. Siempre más barato que tener las herramientas por separado.</p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <motion.div key={plan.name} variants={fadeUp}>
                    <Card className={`h-full flex flex-col hover:shadow-lg transition-all duration-300 ${plan.popular ? 'border-[#2563EB] shadow-md shadow-[#2563EB]/10 ring-1 ring-[#2563EB]/20' : plan.bestValue ? 'border-amber-500 shadow-md shadow-amber-500/10 ring-1 ring-amber-500/20' : 'border-border/50'}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold">{plan.name}</h3>
                          {plan.popular && <Badge className="bg-[#2563EB] text-white">Más popular</Badge>}
                          {plan.bestValue && <Badge className="bg-amber-500 text-white">MAYOR AHORRO</Badge>}
                        </div>
                        <div className="mt-2">
                          <span className="text-3xl font-bold">${plan.price}</span>
                          {plan.duration && <span className="text-muted-foreground text-sm ml-1">/ {plan.duration}</span>}
                        </div>
                        {plan.priceCreditsPM && (
                          <p className="text-xs text-muted-foreground mt-1">
                            o <span className="font-semibold text-foreground">{plan.priceCreditsPM} créditos PM</span>
                          </p>
                        )}
                        {plan.monthly && plan.monthly !== 'Gratis' && (
                          <p className="text-xs text-muted-foreground/70 mt-0.5">{plan.monthly}</p>
                        )}
                        {plan.monthly === 'Gratis' && (
                          <p className="text-xs text-muted-foreground/70 mt-0.5">{plan.monthly}</p>
                        )}
                      </CardHeader>
                      <CardContent className="flex-1 pb-2">
                        <div className="flex items-center gap-2 mb-4">
                          <Users className="size-4 text-[#2563EB]" />
                          <span className="text-sm font-medium">{plan.spectators.toLocaleString()} espectadores</span>
                        </div>
                        <ul className="space-y-2">
                          {plan.features.map(f => (
                            <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <Check className="size-4 text-[#06B6D4] shrink-0 mt-0.5" /> {f}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                      <CardFooter className="pt-2">
 {/* Task D (Sprint 91): pricing CTAs now link
                            to `/register?package=<tier>` (Decision Co-Founder
                            QA #3 — uses `package` query param aligned with
 naming). The value is the tier in
                            lowercase (`gratuito`, `basic`, `bronce`, `plata`,
                            `oro`, `platino`, `diamante`) — derived from
                            `plan.name.toLowerCase()` since the existing plan
                            names already match the tier vocabulary.

                            Pre-fix: all 7 buttons called `goPaquetes` →
                            `/paquetes` (indistinct — the register page had no
                            way to know which package the user clicked).
                            Post-fix: each button carries the tier in the URL
                            so `/register` can pre-select the package (Task
                            D.2 post-deploy verification — if the register page
 doesn't read the param, create ).

                            `Button asChild` wraps `<a href>` so the shadcn
                            Button styling is preserved while gaining a real
                            href for SEO + middle-click + accessibility. */}
                        <Button
                          className={`w-full ${plan.popular ? 'bg-[#2563EB] hover:bg-[#2563EB]/90 text-white' : plan.bestValue ? 'bg-amber-500 hover:bg-amber-500/90 text-white' : ''}`}
                          variant={plan.popular || plan.bestValue ? 'default' : 'outline'}
                          asChild
                        >
                          <a href={`/register?package=${plan.name.toLowerCase()}`}>
                            Comenzar <ArrowRight className="ml-1 size-4" />
                          </a>
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Zoom comparison */}
              <motion.div variants={fadeUp} className="mt-12">
                <Card className="bg-[#0B1437] text-white border-[#2563EB]/20">
                  <CardContent className="p-6 sm:p-8 text-center">
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">Ahorra vs. Zoom + Linktree + Calendly</h3>
                    <p className="text-sm text-white/60 mb-6">Las herramientas por separado cuestan <span className="text-white font-semibold">$184.33/mes ($1,914/año)</span>. Profileia Diamante: <span className="text-[#06B6D4] font-semibold">$82.19/mes ($1,000/año)</span></p>
                    <div className="flex flex-wrap justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2"><Shield className="size-4 text-[#06B6D4]" /> <span>49% más barato</span></div>
                      <div className="flex items-center gap-2"><DollarSign className="size-4 text-[#10B981]" /> <span>+ Plan de compensación</span></div>
                      <div className="flex items-center gap-2"><Zap className="size-4 text-[#8B5CF6]" /> <span>+ Integración nativa</span></div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* PR #1444 (v124.2) — Featured marketplace + coupons visibility from landing */}
        {/* Founder directive: agreguemos el marketplace y los cupones con acceso desde la landing */}
        <LandingFeaturedMarketplace />
        <LandingFeaturedCoupons />

        {/* ===== BONO VIAJE NACIONAL DIAMANTE ===== */}
 {/* Task G (Sprint 91): id="viaje-nacional" added so the
            section is anchorable (#viaje-nacional) — used by deep-links +
            future analytics scroll-tracking. Previously the section had no id. */}
        <section id="viaje-nacional" className="py-20 bg-gradient-to-br from-[#2563EB]/5 via-[#06B6D4]/5 to-[#8B5CF6]/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={stagger}>
              <motion.div variants={fadeUp} className="text-center mb-12">
                <Badge variant="secondary" className="mb-3 bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20">
                  <Star className="size-3 mr-1" /> Bono Exclusivo Diamante
                </Badge>
                <h2 className="text-3xl sm:text-5xl font-bold mb-4">
                  Cada estrella <span className="bg-gradient-to-r from-[#8B5CF6] to-[#2563EB] bg-clip-text text-transparent">🌟</span> = 1 ticket <span className="bg-gradient-to-r from-[#2563EB] to-[#06B6D4] bg-clip-text text-transparent">🎫</span> para Viaje Nacional
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Alcanza Diamante 1⭐, 2⭐, 3⭐ o más y recibe automáticamente 1 ticket por cada estrella para el Viaje Nacional anual organizado por Profileia. Acumulable. No transferible.
                </p>
              </motion.div>

              <motion.div variants={fadeUp} className="grid sm:grid-cols-3 gap-6 mb-12">
                {[
                  {
                    stars: 'Diamante 1⭐',
                    tickets: '1 ticket',
                    desc: 'Viaje Nacional anual — destino revelado cada año por la empresa',
                    color: '#8B5CF6',
                  },
                  {
                    stars: 'Diamante 2⭐',
                    tickets: '2 tickets',
                    desc: 'Acumulable — si no usas el del año anterior, se suma al siguiente',
                    color: '#2563EB',
                  },
                  {
                    stars: 'Diamante 3⭐+',
                    tickets: '3+ tickets',
                    desc: 'Cada estrella adicional = 1 ticket más. ¡Tu red te lleva a viajar!',
                    color: '#06B6D4',
                  },
                ].map((item, i) => (
                  <Card key={i} className="h-full text-center hover:shadow-lg transition-all duration-300 border-border/50 overflow-hidden">
                    <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${item.color}, ${item.color}80)` }} />
                    <CardContent className="p-6">
                      <div className="text-3xl mb-2">🌟</div>
                      <h3 className="text-lg font-bold mb-2" style={{ color: item.color }}>{item.stars}</h3>
                      <p className="text-2xl font-bold mb-3">{item.tickets}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  El Viaje Nacional es un beneficio físico/logístico organizado manualmente por la empresa cada año. Los tickets se asignan automáticamente al alcanzar cada estrella Diamante y se descuentan al confirmar los vuelos al lugar del evento.
                </p>
                <Button size="lg" className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 text-white" onClick={goPaquetes}>
                  Conoce cómo alcanzar Diamante <ArrowRight className="ml-2 size-4" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

 {/* ===== ECONOMÍA ( — flujo canónico) ===== */}
 {/* Doc 02 §2 manda: problema → visión → ecosistema → prueba → economía → oportunidad.
            Esta sección nueva cumple el paso "economía" del flujo. Muestra cómo el
            dinero fluye en Profileia: paquetes → compensación → Market → IA. */}
        <section id="economia" className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge variant="secondary" className="mb-3">
                <DollarSign className="size-3 mr-1" /> Economía Profileia
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Cómo fluye el dinero en Profileia</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Una sola economía conecta paquetes, compensación, Market y créditos PM. Sin caja negra.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-border/50">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center mb-4">
                    <DollarSign className="size-6" />
                  </div>
                  <h3 className="font-semibold mb-2">Paquetes</h3>
                  <p className="text-sm text-muted-foreground">
                    Desde $0 (Gratuito) hasta $1,000 (Diamante). Pagos en USDT (BSC, Polygon, Arbitrum) o créditos PM. 1 crédito = 1 USDT.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center mb-4">
                    <TrendingUp className="size-6" />
                  </div>
                  <h3 className="font-semibold mb-2">8 formas de ganar</h3>
                  <p className="text-sm text-muted-foreground">
                    Bono directo $10, unilevel clientes, ventas (30% estándar / 35% cupones 50-50), reclutador vendedores 2%, matriz viva 2×6, escalación Diamante, viaje nacional Diamante N⭐, compresión dinámica. 8 formas oficiales (ver /plan-de-compensacion).
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center justify-center mb-4">
                    <ShoppingBag className="size-6" />
                  </div>
                  <h3 className="font-semibold mb-2">Profileia Market</h3>
                  <p className="text-sm text-muted-foreground">
                    Vende productos físicos, digitales o servicios. Vendedor recibe 30%, referidor del vendedor 2% de por vida, Profileia 68%.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-10">
              <Button asChild variant="outline">
                <Link href="/plan-de-compensacion">Ver las 8 formas de ganar <ArrowRight className="ml-2 size-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

 {/* ===== TESTIMONIOS ( — Sprint 62 P-219) ===== */}
        {/* Section between pricing (L439) + Bono Diamante (above) + CTA final
            (below). Renders top 6 featured testimonials with Schema.org
 Review markup. Anti-§6: copy 'historias reales' NOT 'ganancias
 garantizadas' (per §13).
 fix: wrapped in SectionErrorBoundary so if the section
            crashes (e.g., API shape change), the rest of the home page
            still renders. */}
        <SectionErrorBoundary fallback={null}>
          <TestimonialsSection />
        </SectionErrorBoundary>

        {/* ===== CTA FINAL ===== */}
        <section id="comunidad" className="py-20 bg-[#0B1437] relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#2563EB]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#8B5CF6]/20 rounded-full blur-3xl" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2 className="text-3xl sm:text-5xl font-bold text-white mb-6">Tu red merece algo mejor que Zoom, ChatGPT y Calendly combinados</h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                Deja de pagar por herramientas separadas que no se comunican. Tu tarjeta, tu sala, tu landing, tu academia, tu market y tu compensación — en un solo enlace.
              </p>
              <Button size="lg" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white text-lg px-8 h-14" onClick={goRegister}>
                Crear mi perfil gratis <ArrowRight className="ml-2 size-5" />
              </Button>
 {/* (P-173) — botón instalar en CTA final */}
              <InstallAppButton variant="default" size="lg" className="bg-[#2563EB] hover:bg-[#2563EB]/90 text-white" />
              <div className="mt-10 flex flex-wrap justify-center gap-8 text-sm text-white/60">
                <span className="flex items-center gap-2"><Shield className="size-4 text-[#06B6D4]" /> 0% comisión en depósitos</span>
                <span className="flex items-center gap-2"><Users className="size-4 text-[#06B6D4]" /> 1,000 espectadores</span>
                <span className="flex items-center gap-2"><Monitor className="size-4 text-[#06B6D4]" /> Grabación $0</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* AdSense — Banner horizontal (visitantes no logueados) */}
        <AdBannerHorizontal />
      </main>

      {/* Auth modal eliminado — los botones ahora redirigen a /login y /register
 reales (). Los formularios mock ya no se necesitan. */}

      {/* Sprint 84 (Founder directive 2026-09-10): the local inline `<footer>`
          block that lived here (lines 722-748 pre-Sprint-84) was removed
          because it duplicated the global `<Footer />` mounted at the root
          layout (`frontend/src/app/layout.tsx:246`). Having both caused the
          bottom of the landing page to look overloaded with two separate
          footer sections stacked vertically. The global `<Footer />` already
          covers Profileia branding, social links, legal links, support +
          community links, and the newsletter signup — so the inline footer
          was redundant. The landing page now ends with `</main>` and the
          global Footer renders below via the root layout. */}

      {/* Sprint 88 — Profileia AI Hub: FAQ assistant widget on the landing
          page. Visible for ALL visitors (anonymous + authenticated). Helps
          convert curious visitors by answering "¿Qué es Profileia?",
          "¿Cuánto cuesta?", etc. The widget is self-contained — if the AI
          Hub is disabled (feature flag off), the widget button still shows
          but messages return 503 (graceful degradation). */}
      <AIChatWidget
        assistantId="faq-negocio"
        pageContext={{ path: '/' }}
        title="Asistente Profileia"
        subtitle="Pregúntame sobre Profileia"
      />
    </div>
  )
}
